import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'TSG eCart';
const DEFAULT_DESCRIPTION = 'Fresh groceries and daily essentials, delivered fast.';

export interface SeoProps {
  title: string;
  description?: string;
  /** Absolute or root-relative canonical path, e.g. `/product/organic-bananas`. Never omit on a page reachable by more than one URL (historical slugs, filtered listings) -- see docs/storefront-seo.md. */
  canonicalPath?: string;
  /** Absolute image URL for Open Graph -- omit if the page has no representative image. */
  image?: string;
  /** `product`, `website`, etc. -- passed straight through to `og:type`. */
  ogType?: string;
  /** Search engines should never index cart/checkout/account pages -- see docs/security-model.md and docs/storefront-seo.md "noindex pages". */
  noindex?: boolean;
  /** Arbitrary JSON-LD structured data (Product, BreadcrumbList, etc.) -- one object or an array, each rendered as its own `<script type="application/ld+json">`. Never fabricate fields (ratings, review counts) this codebase doesn't actually have. */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * The single place every storefront page sets its title/meta/canonical/OG/
 * JSON-LD -- no page component should reach for `document.title` or a
 * one-off `<Helmet>` directly. Built on `react-helmet-async` (the
 * `HelmetProvider` wraps the whole app in `App.tsx`).
 */
export function Seo({ title, description, canonicalPath, image, ogType = 'website', noindex, structuredData }: SeoProps) {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const metaDescription = description ?? DEFAULT_DESCRIPTION;
  const canonicalUrl = canonicalPath
    ? new URL(canonicalPath, typeof window !== 'undefined' ? window.location.origin : 'https://tsgecart.com').toString()
    : undefined;
  const structuredDataList = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      {structuredDataList.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
